import { ThemeEvents } from '@theme/events';

/**
 * <recharge-price-sync>
 *
 * Mirrors the price of the currently selected Recharge purchase option
 * (Subscribe & Save / One-Time, including delivery frequency) into Horizon's
 * native Add to Cart price.
 *
 * The Recharge "Subscription Widget V2" (<recharge-subscription-widget>) renders
 * its options in an open shadow root and marks the chosen one with `rc-selected`.
 * We observe that shadow root and copy the selected option's displayed price, so
 * the button always matches the widget exactly, including on first paint. If the
 * widget can't be read we leave Horizon's server-rendered price in place.
 */
class RechargePriceSync extends HTMLElement {
  #controller = new AbortController();
  /** @type {MutationObserver | undefined} */
  #observer;
  #applyFrame = 0;
  #pollFrame = 0;
  /** @type {ParentNode} */
  #section = document;
  /** @type {Element | null} */
  #widget = null;

  connectedCallback() {
    this.#section = this.closest('.shopify-section') ?? document;
    this.#widget = this.#section.querySelector('recharge-subscription-widget');
    if (!this.#widget) return;

    // A variant change morphs the button (resetting its price) and re-prices the widget.
    this.#section.addEventListener(ThemeEvents.variantUpdate, this.#scheduleApply, {
      signal: this.#controller.signal,
    });

    this.#observeWidget();
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#observer?.disconnect();
    cancelAnimationFrame(this.#applyFrame);
    cancelAnimationFrame(this.#pollFrame);
  }

  // The widget hydrates asynchronously, so poll until its shadow root exists, then
  // observe it. The observer fires on hydration and on every option/frequency change.
  #observeWidget = (tries = 0) => {
    const root = this.#widget?.shadowRoot;
    if (root) {
      this.#observer = new MutationObserver(this.#scheduleApply);
      this.#observer.observe(root, { subtree: true, childList: true, attributes: true });
      this.#scheduleApply();
    } else if (tries < 300) {
      this.#pollFrame = requestAnimationFrame(() => this.#observeWidget(tries + 1));
    }
  };

  #scheduleApply = () => {
    cancelAnimationFrame(this.#applyFrame);
    this.#applyFrame = requestAnimationFrame(this.#apply);
  };

  #apply = () => {
    const selected = this.#widget?.shadowRoot?.querySelector('[rc-selected]');
    const priceEl = selected?.querySelector(
      '[part~="rc-purchase-option__discounted-price"], [part~="rc-purchase-option__price"]'
    );
    if (!priceEl) return;

    // The price span also holds a visually-hidden a11y label; drop it.
    const clone = /** @type {Element} */ (priceEl.cloneNode(true));
    clone.querySelectorAll('.visually-hidden').forEach((node) => node.remove());
    const price = clone.textContent?.trim();
    if (!price) return;

    for (const el of this.#section.querySelectorAll('add-to-cart-component .add-to-cart-price')) {
      const label = el.querySelector('.visually-hidden');
      el.replaceChildren(...(label ? [label] : []), document.createTextNode(price));
    }
  };
}

if (!customElements.get('recharge-price-sync')) {
  customElements.define('recharge-price-sync', RechargePriceSync);
}
